import * as React from 'react';
import PropTypes from 'prop-types';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Alert, CircularProgress, Divider, List, ListItem, ListItemText, TextField } from '@mui/material';
import { canViewTransactionDetails } from '../../helpers/authSession';
import { rulesEngineGet } from '../../services/rulesEngineClient';
import { getRulesEngineUiMessage } from '../../services/rulesEngineErrors';
import { getRulesEngineOperationUrl } from '../../services/rulesEngineEndpoints';
import { askTransactionAssistant, requestCoafReport } from '../../services/transactionAssistant';
import { downloadCoafReportPdf } from '../../services/coafReportPdf';
import { clearSelectedTransaction } from '../../redux/features/selectedTransaction/selectedTransactionSlice';

const StyledText = styled('text', {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme }) => ({
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: {
        variant: 'primary',
      },
      style: {
        fontSize: theme.typography.h5.fontSize,
      },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: {
        fontSize: theme.typography.body2.fontSize,
      },
    },
    {
      props: {
        variant: 'primary',
      },
      style: {
        fontWeight: theme.typography.h5.fontWeight,
      },
    },
    {
      props: ({ variant }) => variant !== 'primary',
      style: {
        fontWeight: theme.typography.body2.fontWeight,
      },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;

  return (
    <React.Fragment>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </React.Fragment>
  );
}

PieCenterLabel.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
};

const MAX_VISIBLE_APPOINTMENTS = 5;
const MIN_ASSISTANT_QUESTION_CHARS = 12;
const MAX_ASSISTANT_QUESTION_CHARS = 500;

function TransactionDetails({ transactionData, graphContext }) {
  const { id } = useParams();
  const canView = canViewTransactionDetails();
  const dispatch = useDispatch();
  const selectedTransaction = useSelector((state) => state.selectedTransaction);
  const [fetchedData, setFetchedData] = React.useState(null);
  const [loadingDetails, setLoadingDetails] = React.useState(false);
  const [detailsErrorMessage, setDetailsErrorMessage] = React.useState('');
  const [assistantQuestion, setAssistantQuestion] = React.useState('Explique em topicos por que essa transacao pode ser suspeita.');
  const [assistantAnswer, setAssistantAnswer] = React.useState('');
  const [assistantError, setAssistantError] = React.useState('');
  const [askingAssistant, setAskingAssistant] = React.useState(false);
  const [generatingReport, setGeneratingReport] = React.useState(false);
  const [reportError, setReportError] = React.useState('');
  const [showAllAppointments, setShowAllAppointments] = React.useState(false);
  const selectedTransactionData = selectedTransaction?.transactionData || {};

  const resolveSelectedOperationId = React.useCallback(() => {
    const selectedData = selectedTransaction?.transactionData;
    const candidate = selectedData?.operation_id
      || selectedData?.operationId
      || selectedData?.operation
      || selectedData?.operationID
      || selectedData?.edge_operation_id
      || selectedData?.edgeOperationId
      || selectedData?.transaction_id
      || selectedData?.transactionId
      || selectedTransaction?.transactionId;

    if (candidate === null || candidate === undefined) {
      return '';
    }

    return String(candidate).trim();
  }, [selectedTransaction]);

  React.useEffect(() => {
    if (!canView) {
      setFetchedData(null);
      setDetailsErrorMessage('Seu perfil não possui permissão para visualizar detalhes da transação.');
      setLoadingDetails(false);
      return undefined;
    }

    const selectedTransactionId = resolveSelectedOperationId();

    if (!selectedTransactionId) {
      setFetchedData(null);
      return;
    }

    let cancelled = false;

    const fetchDetails = async () => {
      try {
        setLoadingDetails(true);
        setDetailsErrorMessage('');
        const transaction = await rulesEngineGet(getRulesEngineOperationUrl(process.env.REACT_APP_API_RULES_URL, selectedTransactionId));

        if (cancelled) return;

        setFetchedData({
          origin: transaction.ordering_name || transaction.sender_name || '',
          destiny: transaction.counterparty_name || transaction.recipient_name || '',
          originDocument: transaction.ordering_id || transaction.source || transaction.origin_document || '',
          destinyDocument: transaction.counterparty_id || transaction.target || transaction.destination_document || '',
          value: transaction.value || transaction.amount
            ? `R$ ${transaction.value || transaction.amount}`
            : '',
          transactioDateTime: transaction.transactioDateTime || transaction.transaction_datetime
            ? new Date(transaction.transactioDateTime || transaction.transaction_datetime).toLocaleString('pt-BR')
            : '',
          countryOrigin: transaction.ordering_location || transaction.origin_location || '',
          destinyOrigin: transaction.counterparty_location || transaction.destination_location || '',
          riskDescription: transaction.riskDescription || transaction.risk_level || '',
          appointments: transaction.alerts_summary?.alerts?.map(alert => alert.description) || [],
        });
      } catch (error) {
        if (!cancelled) {
          setFetchedData(null);
          setDetailsErrorMessage(getRulesEngineUiMessage(error));
        }
      } finally {
        if (!cancelled) setLoadingDetails(false);
      }
    };

    fetchDetails();

    return () => { cancelled = true; };
  }, [canView, resolveSelectedOperationId]);

  const displayData = React.useMemo(() => (
    fetchedData || transactionData || {
      origin: '',
      destiny: '',
      originDocument: '',
      destinyDocument: '',
      value: '',
      transactioDateTime: '',
      countryOrigin: '',
      destinyOrigin: '',
      riskDescription: '',
      appointments: [],
    }
  ), [fetchedData, transactionData]);

  const originDocument = displayData.originDocument
    || displayData.personDocument
    || selectedTransactionData.source
    || selectedTransactionData.ordering_id
    || '-';
  const destinyDocument = displayData.destinyDocument
    || displayData.destinationDocument
    || displayData.counterpartyDocument
    || selectedTransactionData.target
    || selectedTransactionData.counterparty_id
    || '-';
  const activeOperationId = resolveSelectedOperationId() || String(id || '').trim();

  React.useEffect(() => {
    setAssistantAnswer('');
    setAssistantError('');
    setReportError('');
    setShowAllAppointments(false);
  }, [activeOperationId]);

  const appointments = React.useMemo(() => (
    Array.isArray(displayData.appointments) ? displayData.appointments : []
  ), [displayData.appointments]);
  const hasManyAppointments = appointments.length > MAX_VISIBLE_APPOINTMENTS;
  const appointmentsToRender = showAllAppointments
    ? appointments
    : appointments.slice(0, MAX_VISIBLE_APPOINTMENTS);

  const hasTransactionContext = Boolean(
    String(displayData.value || '').trim()
    || String(displayData.transactioDateTime || '').trim()
    || String(displayData.originDocument || displayData.personDocument || selectedTransactionData.source || selectedTransactionData.ordering_id || '').trim()
    || String(displayData.destinyDocument || displayData.destinationDocument || displayData.counterpartyDocument || selectedTransactionData.target || selectedTransactionData.counterparty_id || '').trim()
  );
  const hasGraphContext = Boolean(
    String(graphContext?.currentCenterNodeId || '').trim()
    || Number(graphContext?.totalNodes || 0) > 0
    || Number(graphContext?.totalEdges || 0) > 0
  );
  const hasAppointmentsContext = appointments.length > 0;
  const hasAssistantContext = hasTransactionContext || hasGraphContext || hasAppointmentsContext;

  const normalizedQuestionLength = assistantQuestion.trim().length;
  const hasValidQuestionLength = normalizedQuestionLength >= MIN_ASSISTANT_QUESTION_CHARS;

  const contextSizeScore =
    (hasTransactionContext ? 40 : 0)
    + (hasGraphContext ? 30 : 0)
    + (hasAppointmentsContext ? 30 : 0);

  const estimatedCostLabel = contextSizeScore >= 70 ? 'medio' : 'baixo';

  const assistantBlockedReason = !activeOperationId
    ? 'Selecione uma transacao no grafo para habilitar a consulta.'
    : !hasAssistantContext
      ? 'Inclua contexto: transacao, grafo carregado ou apontamentos.'
      : !hasValidQuestionLength
        ? `A pergunta deve ter ao menos ${MIN_ASSISTANT_QUESTION_CHARS} caracteres.`
        : '';

  const handleAskAssistant = React.useCallback(async () => {
    const normalizedQuestion = assistantQuestion.trim();

    if (!normalizedQuestion || !activeOperationId || askingAssistant || !hasAssistantContext || !hasValidQuestionLength) {
      return;
    }

    try {
      setAskingAssistant(true);
      setAssistantError('');

      const result = await askTransactionAssistant({
        operationId: activeOperationId,
        question: normalizedQuestion,
        transaction: {
          origin: displayData.origin || '',
          destiny: displayData.destiny || '',
          originDocument,
          destinyDocument,
          value: displayData.value || '',
          transactionDateTime: displayData.transactioDateTime || '',
          countryOrigin: displayData.countryOrigin || '',
          destinyOrigin: displayData.destinyOrigin || '',
          riskDescription: displayData.riskDescription || '',
          appointments,
        },
        graphContext: {
          current_center_node_id: graphContext?.currentCenterNodeId || '',
          total_nodes: Number.isFinite(graphContext?.totalNodes) ? graphContext.totalNodes : 0,
          total_edges: Number.isFinite(graphContext?.totalEdges) ? graphContext.totalEdges : 0,
          visible_nodes: Array.isArray(graphContext?.visibleNodes) ? graphContext.visibleNodes : [],
          visible_edges: Array.isArray(graphContext?.visibleEdges) ? graphContext.visibleEdges : [],
        },
        appointmentsContext: {
          total: appointments.length,
          appointments,
        },
        assistantOptions: {
          language: 'pt-BR',
          response_mode: 'objective_with_evidence',
          require_evidence: true,
        },
      });

      setAssistantAnswer(result.text || 'Nenhuma resposta foi retornada pelo assistente.');
    } catch (error) {
      setAssistantError(getRulesEngineUiMessage(error, 'Nao foi possivel consultar o assistente no momento.'));
    } finally {
      setAskingAssistant(false);
    }
  }, [
    activeOperationId,
    askingAssistant,
    appointments,
    assistantQuestion,
    destinyDocument,
    displayData,
    graphContext,
    hasAssistantContext,
    hasValidQuestionLength,
    originDocument,
  ]);

  const handleGenerateReport = React.useCallback(async () => {
    if (!activeOperationId || generatingReport || !hasAssistantContext) {
      return;
    }

    try {
      setGeneratingReport(true);
      setReportError('');

      const result = await requestCoafReport({
        operationId: activeOperationId,
        transaction: {
          origin: displayData.origin || '',
          destiny: displayData.destiny || '',
          originDocument,
          destinyDocument,
          value: displayData.value || '',
          transactionDateTime: displayData.transactioDateTime || '',
          countryOrigin: displayData.countryOrigin || '',
          destinyOrigin: displayData.destinyOrigin || '',
          riskDescription: displayData.riskDescription || '',
          appointments,
        },
        graphContext: {
          current_center_node_id: graphContext?.currentCenterNodeId || '',
          total_nodes: Number.isFinite(graphContext?.totalNodes) ? graphContext.totalNodes : 0,
          total_edges: Number.isFinite(graphContext?.totalEdges) ? graphContext.totalEdges : 0,
          visible_nodes: Array.isArray(graphContext?.visibleNodes) ? graphContext.visibleNodes : [],
          visible_edges: Array.isArray(graphContext?.visibleEdges) ? graphContext.visibleEdges : [],
        },
        appointmentsContext: {
          total: appointments.length,
          appointments,
        },
      });

      const reportText = result.text;

      if (!reportText) {
        setReportError('O assistente nao retornou conteudo para o relatorio.');
        return;
      }

      downloadCoafReportPdf({
        operationId: activeOperationId,
        generatedAt: new Date().toLocaleString('pt-BR'),
        reportText,
        metadata: { institution: displayData.origin || '' },
      });
    } catch (error) {
      setReportError(getRulesEngineUiMessage(error, 'Nao foi possivel gerar o relatorio em PDF no momento.'));
    } finally {
      setGeneratingReport(false);
    }
  }, [
    activeOperationId,
    appointments,
    destinyDocument,
    displayData,
    generatingReport,
    graphContext,
    hasAssistantContext,
    originDocument,
  ]);

  if (!canView) {
    return (
      <Card
        variant="outlined"
        sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}
      >
        <CardContent>
          <Alert severity="warning">Seu perfil não possui permissão para visualizar detalhes da transação.</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}
    >
      <CardContent>
        <Typography component="h2" variant="title">
          Detalhes
        </Typography>

        {selectedTransaction?.transactionId ? (
          <Button
            variant="outlined"
            size="small"
            onClick={() => dispatch(clearSelectedTransaction())}
            sx={{ mt: 2 }}
          >
            Voltar para transação inicial
          </Button>
        ) : null}

        <Divider sx={{ my: 2 }} />

        {detailsErrorMessage ? (
          <Stack sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: 'error.main' }}>
              {detailsErrorMessage}
            </Typography>
          </Stack>
        ) : null}

        {loadingDetails ? (
          <Stack sx={{ alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Carregando detalhes...
            </Typography>
          </Stack>
        ) : (
        <Stack sx={{ gap: 2, flexGrow: 1 }}>
          <Stack sx={{ gap: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: '700' }}>
              Dados cadastrais
            </Typography>
            <Stack sx={{ gap: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: '500' }}>
              Origem:
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', pl: 2 }}>
              - Nome: {displayData.origin || '-'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', pl: 2 }}>
              - Documento: {originDocument}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: '500', mt: 0.5 }}>
              Beneficiário:
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', pl: 2 }}>
              - Nome: {displayData.destiny || '-'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', pl: 2 }}>
              - Documento: {destinyDocument}
            </Typography>
            </Stack>
          </Stack>

          <Stack sx={{ gap: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: '700' }}>
              Dados da transação
            </Typography>
            <Stack sx={{ gap: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: '500' }}>
                Valor:
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {displayData.value}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: '500' }}>
                Data Hora Transação:
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {displayData.transactioDateTime}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: '500' }}>
                Localização Origem:
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {displayData.countryOrigin}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: '500' }}>
                Localização Destino:
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {displayData.destinyOrigin}
              </Typography>
            </Stack>
            </Stack>
          </Stack>

          <Stack sx={{ gap: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: '700' }}>
              Apontamentos ({appointments.length})
            </Typography>
            <Stack sx={{ gap: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <List sx={{ listStyleType: 'disc', color: 'text.secondary', maxHeight: 220, overflowY: 'auto' }}>
              {appointments.length > 0 ? (
                appointmentsToRender.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={item} sx={{ display: 'list-item' }} />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="Nenhum apontamento" sx={{ fontStyle: 'italic' }} />
                </ListItem>
              )}
            </List>
            {hasManyAppointments ? (
              <Button
                size="small"
                variant="text"
                onClick={() => setShowAllAppointments((current) => !current)}
                sx={{ alignSelf: 'flex-start' }}
              >
                {showAllAppointments ? 'Mostrar menos' : `Ver todos (${appointments.length})`}
              </Button>
            ) : null}
            </Stack>
          </Stack>

          <Stack sx={{ gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: '700' }}>
              Assistente AML
            </Typography>
            <Stack sx={{ gap: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <TextField
              label="Pergunta"
              value={assistantQuestion}
              onChange={(event) => setAssistantQuestion(event.target.value)}
              multiline
              minRows={3}
              maxRows={6}
              size="small"
              fullWidth
              disabled={askingAssistant || !activeOperationId}
              inputProps={{ maxLength: MAX_ASSISTANT_QUESTION_CHARS }}
              helperText={`${normalizedQuestionLength}/${MAX_ASSISTANT_QUESTION_CHARS} caracteres`}
            />
            <Button
              variant="contained"
              onClick={handleAskAssistant}
              disabled={askingAssistant || Boolean(assistantBlockedReason)}
            >
              {askingAssistant ? 'Consultando...' : 'Perguntar ao Assistente'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleGenerateReport}
              disabled={generatingReport || !activeOperationId || !hasAssistantContext}
            >
              {generatingReport ? 'Gerando relatorio...' : 'Gerar Relatório PDF (COAF)'}
            </Button>

            {generatingReport ? (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: 'action.hover',
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Gerando relatorio COAF com o assistente...
                </Typography>
              </Stack>
            ) : null}

            {reportError ? (
              <Typography variant="body2" sx={{ color: 'error.main' }}>
                {reportError}
              </Typography>
            ) : null}

            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Contexto: transacao {hasTransactionContext ? 'ok' : 'pendente'} | grafo {hasGraphContext ? 'ok' : 'pendente'} | apontamentos {hasAppointmentsContext ? 'ok' : 'pendente'} | custo estimado: {estimatedCostLabel}
            </Typography>

            {assistantBlockedReason ? (
              <Typography variant="body2" sx={{ color: 'warning.main' }}>
                {assistantBlockedReason}
              </Typography>
            ) : null}

            {askingAssistant ? (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: 'action.hover',
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Consulta enviada. Aguardando resposta do assistente...
                </Typography>
              </Stack>
            ) : null}

            {assistantError ? (
              <Typography variant="body2" sx={{ color: 'error.main' }}>
                {assistantError}
              </Typography>
            ) : null}

            {assistantAnswer ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                {assistantAnswer}
              </Typography>
            ) : null}
            </Stack>
          </Stack>
        </Stack>
        )}
      </CardContent>
    </Card>
  );
}

TransactionDetails.propTypes = {
  transactionData: PropTypes.object,
  graphContext: PropTypes.shape({
    currentCenterNodeId: PropTypes.string,
    totalNodes: PropTypes.number,
    totalEdges: PropTypes.number,
    visibleNodes: PropTypes.array,
    visibleEdges: PropTypes.array,
  }),
};

export default TransactionDetails;
